// Colour-blind safety for colours that are not theme tokens.
//
// The theme's own roles (`var(--c-*)`) already switch to the Wong (2011) status colours in the
// colour-blind theme. A hex written in a page — a framework's badge colour, a jurisdiction, a
// department swatch, a status a page coloured itself — does not, and red beside green is the one
// pairing most colour-blind people cannot tell apart. While the colour-blind theme is on, cbSafe()
// turns every red onto vermilion and every green onto blue (the Wong pair the status tokens use),
// keeping each colour's saturation and lightness so a tint stays a tint and a dark stays dark.
// Every other hue, and every colour in the other themes, is returned unchanged.
//
// ThemeProvider sets the flag during render, and the app roots read the theme mode, so a switch
// re-renders every page with the new colours.

let colourBlind = false;

/** Called by ThemeProvider whenever it renders. */
export function setColourBlindColours(on: boolean): void {
  colourBlind = on;
}

export function colourBlindColoursOn(): boolean {
  return colourBlind;
}

const VERMILION_HUE = 26;   // #D55E00
const BLUE_HUE = 202;       // #0072B2

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
  const l = (mx + mn) / 2;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  let h = 0;
  if (d) {
    if (mx === r) h = 60 * (((g - b) / d) % 6);
    else if (mx === g) h = 60 * ((b - r) / d + 2);
    else h = 60 * ((r - g) / d + 4);
  }
  return [h < 0 ? h + 360 : h, s, l];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const [r, g, b] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x]
    : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return [r + m, g + m, b + m];
}

/** The hue a red or green moves to, or null when the colour is neither. */
function targetHue(r: number, g: number, b: number): number | null {
  const [h, s, l] = rgbToHsl(r, g, b);
  if (s < 0.2 || l < 0.06 || l > 0.97) return null;    // greys, near-black, near-white
  if (h <= 15 || h >= 340) return VERMILION_HUE;
  if (h >= 70 && h <= 170) return BLUE_HUE;
  return null;
}

function shift(r: number, g: number, b: number): [number, number, number] | null {
  const hue = targetHue(r, g, b);
  if (hue === null) return null;
  const [, s, l] = rgbToHsl(r, g, b);
  // A bright red at its own lightness turns orange and reads as the amber "partly met" colour;
  // mid tones go down to vermilion's depth (#D55E00) instead. Tints and darks keep theirs.
  const depth = hue === VERMILION_HUE && l > 0.3 && l < 0.75 ? Math.min(l, 0.42) : l;
  return hslToRgb(hue, s, depth);
}

const hex2 = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 255).toString(16).padStart(2, "0");

function shiftHex(hex: string): string {
  const body = hex.slice(1);
  const full = body.length === 3 || body.length === 4 ? body.split("").map((c) => c + c).join("") : body;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16) / 255);
  const moved = shift(r, g, b);
  if (!moved) return hex;
  const alpha = full.length === 8 ? full.slice(6) : "";
  return `#${moved.map(hex2).join("")}${alpha}`;
}

function shiftRgb(match: string, fn: string, args: string): string {
  const parts = args.split(",").map((p) => p.trim());
  const [r, g, b] = parts.slice(0, 3).map((p) => Number(p) / 255);
  if ([r, g, b].some((v) => Number.isNaN(v))) return match;
  const moved = shift(r, g, b);
  if (!moved) return match;
  const rgb = moved.map((v) => Math.round(v * 255)).join(", ");
  return parts.length > 3 ? `${fn}(${rgb}, ${parts[3]})` : `${fn}(${rgb})`;
}

/**
 * The colour-blind-safe form of a colour, or of every colour inside a CSS value
 * ("1px solid #7f1d1d", "rgba(22,163,74,0.25)"). Unchanged outside the colour-blind theme.
 */
export function cbSafe<T extends string | undefined | null>(value: T): T {
  if (!colourBlind || !value) return value;
  return value
    .replace(/#(?:[0-9a-fA-F]{8}|[0-9a-fA-F]{6}|[0-9a-fA-F]{3,4})\b/g, shiftHex)
    .replace(/\b(rgba?)\(([^)]*)\)/g, shiftRgb) as T;
}

/**
 * A colour table whose string values are passed through cbSafe() each time they are read, so a
 * module-level map of badge colours follows the theme without changing any of its readers
 * (`FW_COLOR[id] + "22"` still works). Nested objects and arrays are wrapped the same way.
 */
export function cbPalette<T extends object>(table: T): T {
  // One proxy per object, so a row read twice is the same object (React compares dependencies
  // by identity; a fresh proxy on every read would re-run effects for ever).
  const cached = proxies.get(table);
  if (cached) return cached as T;
  const proxy = new Proxy(table, {
    get(target, key, receiver) {
      const value = Reflect.get(target, key, receiver);
      // Only short values that can hold a colour are rewritten; a table's prose (law notes,
      // labels) is returned as it is, so a large table costs nothing to read.
      if (typeof value === "string") return value.length <= 64 && /#|rgb/.test(value) ? cbSafe(value) : value;
      if (value && typeof value === "object") return cbPalette(value as object);
      return value;
    },
  });
  proxies.set(table, proxy);
  return proxy;
}

const proxies = new WeakMap<object, object>();
